import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Loader2, Check, Zap, Crown, Video, Clock, Share2, Target, AlertCircle, Sparkles, ChevronRight, Users, MessageCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EOD = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    plan: 'basic',
    instructions: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const plans = [
    {
      id: 'basic',
      name: 'Basic',
      price: 499,
      icon: Zap,
      badge: 'Best for quick highlight sharing',
      features: [
        '1 highlight reel (30-40 sec)',
        'Basic cuts & music',
        'No text overlays',
        '1 revision',
        'Delivery in 72 hours'
      ]
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 799,
      icon: Crown,
      badge: 'Best for strong personal branding',
      features: [
        '1 highlight reel (45-60 sec)',
        'Advanced cuts + music sync',
        'Text overlays (name, key moments)',
        '2 revisions',
        'Priority editing',
        'Delivery in 48 hours'
      ]
    }
  ];

  const benefits = [
    { icon: Video, text: 'Clean, engaging highlight reels' },
    { icon: Sparkles, text: 'Smooth cuts & music sync' },
    { icon: Share2, text: 'Social media ready format (Reel/Shorts)' },
    { icon: Clock, text: 'Fast delivery within 48 hours' }
  ];

  const videoTestimonials = [
    {
      id: 'ncVMMqtKsyg',
      url: 'https://www.youtube.com/embed/ncVMMqtKsyg',
      type: 'Pro Edit',
      badge: 'premium'
    },
    {
      id: 'HE_rL6QQhHQ',
      url: 'https://www.youtube.com/embed/HE_rL6QQhHQ',
      type: 'Pro Edit',
      badge: 'premium'
    },
    {
      id: 'GLIBgZgJdU4',
      url: 'https://www.youtube.com/embed/GLIBgZgJdU4',
      type: 'Pro Edit',
      badge: 'premium'
    },
    {
      id: 'wapQqBS058Q',
      url: 'https://www.youtube.com/embed/wapQqBS058Q',
      type: 'Basic Edit',
      badge: 'basic'
    }
  ];

  const whyChooseUs = [
    'Fast turnaround',
    'Experience with sports edits',
    'Designed for social media engagement'
  ];

  const importantNotes = [
    'Clips should be pre-selected (max 10–15 clips)',
    'Raw long videos will not be accepted',
    'Delivery timeline starts after receiving all files',
    'Additional revisions may be chargeable'
  ];

  const selectedPlan = plans.find(p => p.id === formData.plan);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!formData.phone.trim() || formData.phone.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/eod/create-order`, {
        name: formData.name,
        phone: formData.phone,
        plan: selectedPlan.name,
        amount: selectedPlan.price,
        instructions: formData.instructions
      });

      const { payment_session_id, order_id } = response.data;

      // Redirect to Cashfree payment page
      const cashfree = window.Cashfree({
        mode: 'production'
      });

      cashfree.checkout({
        paymentSessionId: payment_session_id,
        redirectTarget: '_self',
        returnUrl: `${window.location.origin}/eod/success?order_id=${order_id}`
      });
    } catch (err) {
      console.error('Payment initiation error:', err);
      setError(err.response?.data?.detail || 'Failed to initiate payment. Please try again.');
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50">
      {/* Header Spacer */}
      <div className="h-20"></div>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/917739303973?text=Hi!%20I%20want%20to%20know%20more%20about%20your%20video%20editing%20services"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 flex items-center gap-2 group"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="hidden group-hover:inline-block text-sm font-semibold mr-2">Chat with us</span>
      </a>

      {/* HERO SECTION */}
      <div className="relative bg-gradient-to-br from-red-600 via-red-700 to-red-900 text-white py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-semibold mb-6">
            🚀 Professional Cricket Highlights
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Turn Your Match Performance Into a Powerful Highlight Video
          </h1>
          <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-3xl mx-auto">
            Get your personalized cricket highlight reel edited in just 48 hours — built to showcase your game and stand out on social media.
          </p>
          <Button
            onClick={() => document.getElementById('order-form').scrollIntoView({ behavior: 'smooth' })}
            size="lg"
            className="bg-white text-red-700 hover:bg-slate-100 shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 text-lg px-8 py-6 h-auto"
          >
            Get Your Video Now
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* WHAT YOU GET */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">🎥 What You Get</h2>
          <p className="text-slate-600 text-lg">Professional quality, delivered fast</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, idx) => {
            const Icon = benefit.icon;
            return (
              <Card key={idx} className="border-slate-200 hover:shadow-lg transition-shadow">
                <CardContent className="pt-6 text-center">
                  <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-red-600" />
                  </div>
                  <p className="text-slate-700 font-medium">{benefit.text}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* VIDEO TESTIMONIALS / EXAMPLES */}
      <div className="max-w-7xl mx-auto px-6 py-16 bg-gradient-to-br from-slate-100 to-slate-50">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">🎬 See Our Work</h2>
          <p className="text-slate-600 text-lg">
            Real examples from our clients • Basic & Pro edits compared
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {videoTestimonials.map((video) => (
            <Card key={video.id} className="border-slate-200 hover:shadow-xl transition-all duration-300 overflow-hidden group">
              <div className="relative">
                <div className="aspect-[9/16] bg-black">
                  <iframe
                    src={video.url}
                    title={`${video.type} example`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="absolute top-3 right-3">
                  <span className={`px-3 py-1.5 rounded-full text-xs font-bold shadow-lg ${
                    video.badge === 'premium' 
                      ? 'bg-gradient-to-r from-red-600 to-red-700 text-white' 
                      : 'bg-gradient-to-r from-cyan-500 to-cyan-600 text-white'
                  }`}>
                    {video.type}
                  </span>
                </div>
              </div>
              <CardContent className="p-4 text-center">
                <p className="text-sm text-slate-600">
                  {video.badge === 'premium' 
                    ? '✨ Advanced editing with text overlays & effects' 
                    : '⚡ Quick turnaround with clean cuts'}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="text-center mt-8">
          <p className="text-slate-700 font-medium text-lg">
            Choose the style that matches your needs 👇
          </p>
        </div>
      </div>

      {/* PACKAGES SECTION */}
      <div id="packages" className="max-w-6xl mx-auto px-6 py-16 bg-white/50">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">💼 Choose Your Package</h2>
          <p className="text-slate-600 text-lg">Select the plan that fits your needs</p>
        </div>
        <div className="grid lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan) => {
            const Icon = plan.icon;
            const isSelected = formData.plan === plan.id;
            return (
              <Card
                key={plan.id}
                className={`cursor-pointer transition-all duration-300 hover:shadow-2xl ${
                  isSelected
                    ? 'border-red-600 shadow-2xl shadow-red-500/30 ring-4 ring-red-600 scale-105'
                    : 'border-slate-200 hover:border-red-400'
                }`}
                onClick={() => handleInputChange('plan', plan.id)}
              >
                <CardHeader className="text-center pb-4">
                  <div className="flex justify-center mb-4">
                    <div className={`p-4 rounded-2xl ${
                      isSelected ? 'bg-red-100' : 'bg-slate-100'
                    }`}>
                      <Icon className={`w-10 h-10 ${
                        isSelected ? 'text-red-600' : 'text-slate-600'
                      }`} />
                    </div>
                  </div>
                  <CardTitle className="text-2xl font-bold">{plan.name}</CardTitle>
                  <div className="text-4xl font-bold text-red-600 mt-2">₹{plan.price}</div>
                  <div className={`inline-block mt-3 px-3 py-1 rounded-full text-xs font-semibold ${
                    isSelected ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'
                  }`}>
                    {plan.badge}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                        isSelected ? 'text-red-600' : 'text-green-600'
                      }`} />
                      <span className="text-slate-700">{feature}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* IMPORTANT NOTES */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <Card className="border-yellow-200 bg-yellow-50/50 mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertCircle className="w-6 h-6 text-yellow-700" />
              </div>
              <CardTitle className="text-2xl text-yellow-900">📌 Important Notes</CardTitle>
            </div>
            <CardDescription className="text-yellow-800 text-base">
              Please read carefully before placing your order
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {importantNotes.map((note, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-yellow-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-yellow-900 font-bold text-sm">{idx + 1}</span>
                  </div>
                  <span className="text-yellow-900 font-medium">{note}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 p-4 bg-yellow-100 border border-yellow-300 rounded-lg">
              <p className="text-yellow-900 font-semibold text-center">
                ⚡ This protects you from chaos and ensures smooth delivery
              </p>
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp Contact Card */}
        <Card className="border-green-200 bg-gradient-to-br from-green-50 to-green-100/50 shadow-lg">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-full mb-4">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Have Questions?</h3>
              <p className="text-slate-600 mb-4">
                Chat with us on WhatsApp for instant answers about packages, delivery, or custom requirements
              </p>
              <a
                href="https://wa.me/917739303973?text=Hi!%20I%20want%20to%20know%20more%20about%20your%20video%20editing%20services"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-green-500 hover:bg-green-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  WhatsApp: 7739303973
                </Button>
              </a>
              <p className="text-xs text-slate-500 mt-3">Quick response during business hours</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* WHO IS THIS FOR */}
      <div className="max-w-6xl mx-auto px-6 py-16 bg-white/50">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">🎯 Who Is This For?</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {['Corporate Strikers players', 'League players', 'Anyone wanting to showcase their performance'].map((audience, idx) => (
            <Card key={idx} className="border-slate-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="w-14 h-14 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-7 h-7 text-cyan-600" />
                </div>
                <p className="text-slate-700 font-semibold">{audience}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* WHY CHOOSE US */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <Card className="border-slate-200 shadow-xl">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold text-slate-800">⭐ Why Choose Us?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {whyChooseUs.map((reason, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <span className="text-slate-800 font-medium text-lg">{reason}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ORDER FORM SECTION */}
      <div id="order-form" className="max-w-6xl mx-auto px-6 py-16 bg-gradient-to-br from-slate-100 to-red-50">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent mb-4">
            🚀 Ready to Get Started?
          </h2>
          <p className="text-lg text-slate-600">
            Your best shots deserve better presentation. Fill in your details below.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Selected Plan Display */}
          <div>
            <Card className="shadow-xl mb-6">
              <CardHeader>
                <CardTitle className="text-2xl">Your Selected Plan</CardTitle>
                <CardDescription>Review your choice</CardDescription>
              </CardHeader>
              <CardContent>
                {plans.filter(p => p.id === formData.plan).map(plan => {
                  const Icon = plan.icon;
                  return (
                    <div key={plan.id} className="p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-lg border-2 border-red-600">
                      <div className="flex items-center gap-3 mb-4">
                        <Icon className="w-8 h-8 text-red-600" />
                        <div>
                          <h3 className="text-xl font-bold text-slate-800">{plan.name} Plan</h3>
                          <p className="text-3xl font-bold text-red-600">₹{plan.price}</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {plan.features.slice(0, 3).map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                            <Check className="w-4 h-4 text-green-600" />
                            {feature}
                          </div>
                        ))}
                      </div>
                      <Button
                        variant="outline"
                        className="w-full mt-4 border-red-600 text-red-600 hover:bg-red-50"
                        onClick={() => document.getElementById('packages').scrollIntoView({ behavior: 'smooth' })}
                      >
                        Change Plan
                      </Button>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Order Form */}
          <div>
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl">Your Details</CardTitle>
                <CardDescription>
                  Fill in your information to proceed with payment
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="Enter your phone number"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="instructions">Special Instructions (Optional)</Label>
                    <Textarea
                      id="instructions"
                      placeholder="Tell us about your video editing requirements..."
                      value={formData.instructions}
                      onChange={(e) => handleInputChange('instructions', e.target.value)}
                      disabled={isLoading}
                      rows={4}
                    />
                  </div>

                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {error}
                    </div>
                  )}

                  {/* Order Summary */}
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-600">Plan</span>
                      <span className="font-semibold text-slate-800">{selectedPlan.name}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-slate-300">
                      <span className="text-lg font-semibold text-slate-800">Total Amount</span>
                      <span className="text-2xl font-bold text-red-600">₹{selectedPlan.price}</span>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg shadow-red-500/30 hover:shadow-xl h-12 text-lg"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      `Pay ₹${selectedPlan.price}`
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EOD;